/* eslint-disable max-len */
import {SERVICE_NAMES} from '../constants';
import {createApiCall} from '../utils';
import type {ServiceName} from '../constants';
import type {
    ApiCallOptions,
    PaginatedResponse,
    CustomField,
    AccountUserCustomField,
    CreateCustomFieldRequest,
    UpdateCustomFieldRequest,
    UpdateUserCustomFieldValueRequest,
    CustomFieldsListParams
} from './api.d';

/**
 * Custom Field Service API client.
 *
 * Source: https://custom-field-service.dev.validsign.eu/api/documentation/api.json
 * Synced: 2026-04-09
 */

const createCustomFieldApiCall = <T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => createApiCall<T>(SERVICE_NAMES.CUSTOM_FIELD as ServiceName, method, endpoint);

export const customFieldApi = {
    // ========== Account-level custom field definitions ==========

    /** GET /accounts/{accountId}/custom-fields — list custom field definitions for an account */
    getCustomFields: ({accountId, params, ...options}: {accountId: string; params?: CustomFieldsListParams} & ApiCallOptions) =>
        createCustomFieldApiCall<PaginatedResponse<CustomField>>('GET', `accounts/${accountId}/custom-fields`)({...options, params}),

    /** GET /accounts/{accountId}/custom-fields/{customField} — get single custom field definition */
    getCustomField: ({accountId, customFieldId, ...options}: {accountId: string; customFieldId: string} & ApiCallOptions) =>
        createCustomFieldApiCall<CustomField>('GET', `accounts/${accountId}/custom-fields/${customFieldId}`)({...options}),

    /** POST /accounts/{accountId}/custom-fields — create custom field definition */
    createCustomField: ({accountId, data, ...options}: {accountId: string; data: CreateCustomFieldRequest} & ApiCallOptions) =>
        createCustomFieldApiCall<CustomField>('POST', `accounts/${accountId}/custom-fields`)({...options, data}),

    /** PUT /accounts/{accountId}/custom-fields/{customField} — update custom field definition */
    updateCustomField: ({
        accountId, customFieldId, data, ...options
    }: {accountId: string; customFieldId: string; data: UpdateCustomFieldRequest} & ApiCallOptions) =>
        createCustomFieldApiCall<CustomField>('PUT', `accounts/${accountId}/custom-fields/${customFieldId}`)({...options, data}),

    /** DELETE /accounts/{accountId}/custom-fields/{customField} */
    deleteCustomField: ({accountId, customFieldId, ...options}: {accountId: string; customFieldId: string} & ApiCallOptions) =>
        createCustomFieldApiCall<void>('DELETE', `accounts/${accountId}/custom-fields/${customFieldId}`)({...options}),

    // ========== Per-user custom field values ==========

    /** GET /account-users/{accountUserId}/custom-fields — list values for an account user */
    getUserCustomFields: ({accountUserId, ...options}: {accountUserId: string} & ApiCallOptions) =>
        createCustomFieldApiCall<AccountUserCustomField[]>('GET', `account-users/${accountUserId}/custom-fields`)({...options}),

    /** GET /account-users/{accountUserId}/custom-fields/{customField} */
    getUserCustomField: ({
        accountUserId, customFieldId, ...options
    }: {accountUserId: string; customFieldId: string} & ApiCallOptions) =>
        createCustomFieldApiCall<AccountUserCustomField>('GET', `account-users/${accountUserId}/custom-fields/${customFieldId}`)({...options}),

    /** PUT /account-users/{accountUserId}/custom-fields/{customField} — update user-level value */
    updateUserCustomFieldValue: ({
        accountUserId, customFieldId, data, ...options
    }: {accountUserId: string; customFieldId: string; data: UpdateUserCustomFieldValueRequest} & ApiCallOptions) =>
        createCustomFieldApiCall<AccountUserCustomField>('PUT', `account-users/${accountUserId}/custom-fields/${customFieldId}`)({...options, data})
};
